import cloneDeep from 'lodash/cloneDeep'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'
import get from 'lodash/get'
import has from 'lodash/has'
import indexOf from 'lodash/indexOf'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import slice from 'lodash/slice'
import startsWith from 'lodash/startsWith'
import moment from 'moment'

export function removeNoindexMeta() {
  const metaTag = document.querySelector('meta[name="robots"]')
  if (metaTag) {
    metaTag.remove()
  }
}

export function openInNewTab(url, target) {
  window.open(url, target ?? '_blank', 'noopener,noreferrer')
}

export function formHasOfficeUseOnlyQuestions(form) {
  if (!form) {
    return false
  }

  return form.pages.some((p) => {
    if (p.sections.rows) {
      return p.sections.some((s) =>
        s.rows.some((r) => r.questions.some((q) => q.is_office_use_only))
      )
    }
    return p.sections.some((s) => s.questions.some((q) => q.is_office_use_only))
  })
}

export function packetHasOfficeUseQuestions(packet, forms, documents = []) {
  const formsInPacket = forms.filter((f) => packet.forms.includes(f.id))
  const documentInPackets = documents.filter((d) =>
    packet.documents.includes(d.id)
  )
  return (
    formsInPacket.some((f) => f.has_office_use_questions) ||
    documentInPackets.some((d) => d.has_office_use_questions)
  )
}

export function addToArrayIfEvery(arr, value, functor) {
  if (!arr) {
    return arr
  }

  if (arr.every((x) => functor(x))) {
    return [...arr, value]
  }

  return arr
}

export function removeFromArrayIf(arr, functor) {
  if (!arr) {
    return arr
  }

  return arr.filter((x) => !functor(x))
}

export function arrayUpdateByKey(arr, key, value, newItem, addIfNotFound) {
  if (isNil(newItem)) return arr
  if (!isFilledArray(arr)) {
    return addIfNotFound ? [newItem] : arr
  }
  if (!hasText(key) || isNil(value)) {
    return addIfNotFound ? [...arr, newItem] : arr
  }
  const newArr = cloneDeep(arr)
  const target = findIndex(arr, (item) => item?.[key] === value)
  if (target === -1) return addIfNotFound ? [...arr, newItem] : arr
  newArr[target] = newItem
  return newArr
}

export function stringToNumber(s) {
  if (!s) {
    return null
  }

  const parsed = parseFloat(s)
  return !isNaN(parsed) ? parsed : null
}

export function isNullOrEmpty(str) {
  return !str || str.length === 0 || !str.trim()
}

export const hasText = (s) => typeof s === 'string' && s.trim().length > 0
export const isFilledArray = (a) => Array.isArray(a) && a.length > 0

export function removeArrayIndex(array, index) {
  return [...slice(array, 0, index), ...slice(array, index + 1, array.length)]
}

export function updateSearchOrder(prevProps, prevOrders, propSelected) {
  const propIndex = indexOf(prevProps, propSelected)
  if (propIndex === -1) return [prevProps, prevOrders]
  if (propIndex === 0) {
    // eslint-disable-next-line
    const [first, ...restOrders] = prevOrders
    const nextFirst = prevOrders[0] === 'asc' ? 'desc' : 'asc'
    return [prevProps, [nextFirst, ...restOrders]]
  }
  const nextProps = [propSelected, ...removeArrayIndex(prevProps, propIndex)]
  const nextOrders = ['asc', ...removeArrayIndex(prevOrders, propIndex)]
  return [nextProps, nextOrders]
}

export function handleSearch(
  searchTerm,
  list,
  propsToSearch,
  propsToSort,
  propsOrders
) {
  if (!hasText(searchTerm)) {
    return orderBy(list, propsToSort, propsOrders)
  }

  const filtered = list.filter((item) =>
    propsToSearch.some((prop) => {
      const value = String(get(item, prop, ''))
      return (
        hasText(value) && value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })
  )

  return orderBy(filtered, propsToSort, propsOrders)
}

export function updateServerSearchOrder(key, prevOrders) {
  let searchOrder = prevOrders?.find((o) => Object.keys(o)[0] === key)
  if (isNil(searchOrder)) {
    searchOrder = { [key]: 'desc' }
  } else {
    searchOrder = { [key]: searchOrder[key] === 'asc' ? 'desc' : 'asc' }
  }
  const removed = prevOrders.filter((o) => Object.keys(o)[0] !== key)
  return [searchOrder, ...removed]
}

export function handleServerSearch({
  searchTerm = '',
  searchOrders = [],
  paginated = false,
  firstNameTerm = null,
  lastNameTerm = null,
}) {
  const search = hasText(searchTerm) ? searchTerm : null
  const order = searchOrders.reduce((acc, order, index) => {
    const key = Object.keys(order)[0]
    const value = Object.values(order)[0]
    if (index === 0) {
      return `${value === 'desc' ? '-' : ''}${key}`
    }
    return `${acc},${value === 'desc' ? '-' : ''}${key}`
  }, '')
  return {
    search,
    order,
    paginated,
    firstName: firstNameTerm,
    lastName: lastNameTerm,
  }
}

export function getImageUrl(path) {
  const initialPath = process.env.REACT_APP_IMAGE_URL

  if (!initialPath) return path

  if (startsWith(path, initialPath)) return path
  return `${initialPath}${path}`
}

export function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d)
}

export function buildFullName(firstName, lastName) {
  const hasFirst = hasText(firstName)
  const hasLast = hasText(lastName)
  if (!hasFirst) {
    return hasLast ? lastName : ''
  }
  if (!hasLast) {
    return hasFirst ? firstName : ''
  }
  return `${firstName} ${lastName}`
}

export function buildFormDownloadName(firstName, lastName, formName) {
  const userName =
    hasText(firstName) || hasText(lastName)
      ? `${buildFullName(firstName, lastName)} - `
      : ''
  return `${userName}${formName}`
}

export function filterValidMessages(messages) {
  return filter(
    messages,
    (m) =>
      (has(m, ['response', 'is_submitted']) &&
        m.response.is_submitted === false) ||
      (has(m, ['packet_response', 'is_submitted']) &&
        m.packet_response.is_submitted === false)
  )
}

export function mapMessageResponses(messages) {
  return map(messages, ({ response, packet_response }) =>
    isNil(response) ? packet_response : response
  )
}

export function mapClient(client) {
  return {
    ...client,
    full_name: buildFullName(client?.first_name, client?.last_name),
    date_birth_us: hasText(client?.date_birth)
      ? moment(client.date_birth).format('MM-DD-YYYY')
      : '',
  }
}

export function momentLocal(date, format) {
  return moment.utc(date, format).local()
}

export function getTableRow(answer) {
  return answer.question_row ?? answer.answer_matrix_other_row ?? {}
}

export function getMatrixCoordinateId(coordinate) {
  if (isNil(coordinate)) return null
  if (isObject(coordinate)) {
    return coordinate?.id ?? null
  }
  return coordinate ?? null
}

export function objectHasPropWithValue(obj, value) {
  return isObject(obj) && Object.values(obj).some((err) => err === value)
}

const getCleanObject = (object) => {
  if (!Array.isArray(object)) {
    if (object.isNew && object.id) {
      delete object.id
    }
    return object
  }

  return object.map((item) => {
    const { id, ...childObject } = item
    if (!childObject.isNew) {
      childObject.id = id
    }
    Object.keys(childObject).forEach((key) => {
      if (Array.isArray(childObject[key])) {
        childObject[key] = getCleanObject(childObject[key])
      }
    })
    return childObject
  })
}

export const getObjectWithOrWithoutId = (getterFunction, objectId) => {
  const { id, ...object } = getterFunction(objectId)

  if (!object.isNew) {
    object.id = id
  }

  if (
    object.isNew &&
    !object.parent_page_id &&
    !object.page &&
    object.allow_empty
  ) {
    object.uid = id
  }

  const objPropertiesWithId = [
    'question_columns',
    'question_rows',
    'question_choices',
    'conditional_rule_groups',
    'elation_integration',
    'athena_integration',
    'chirotouch_integration',
    'open_dental_integration',
  ]

  objPropertiesWithId.forEach((property) => {
    if (!object[property]) return

    object[property] = getCleanObject(object[property])
  })

  return object
}

export function reorder(list, fromIndex, toIndex) {
  const result = Array.from(list)
  const [removed] = result.splice(fromIndex, 1)
  result.splice(toIndex, 0, removed)

  return result
}
